import { useEffect, useState } from "react";
import { createCategory, updateCategory } from "../../api/courts";
import { IReservationCategory } from "../../models/ICourtReservation";
import { Alert, Button, Checkbox, Dialog, Field, Input } from "../../ui";
import "./styles.scss";

type CategoryDialogProps = {
  category?: IReservationCategory;
  isOpen: boolean;
  onClose: (update?: boolean) => void;
};

const CategoryDialog = (props: CategoryDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    pricePerHour: 350,
    isActive: true,
    earnsClashPoints: false,
  });

  useEffect(() => {
    if (props.isOpen) {
      setErrorMessage("");

      if (props.category) {
        setFormData({
          name: props.category.name,
          pricePerHour: props.category.pricePerHour,
          isActive: props.category.isActive,
          earnsClashPoints: props.category.earnsClashPoints,
        });
      } else {
        setFormData({
          name: "",
          pricePerHour: 350,
          isActive: true,
          earnsClashPoints: false,
        });
      }
    }
  }, [props.isOpen, props.category]);

  const handleClose = (update?: boolean) => {
    props.onClose(update);
    setErrorMessage("");
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setErrorMessage("Category name is required");
      return;
    }

    if (formData.pricePerHour <= 0) {
      setErrorMessage("Price per hour must be greater than 0");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      if (props.category) {
        await updateCategory(props.category.id, {
          name: formData.name.trim(),
          pricePerHour: formData.pricePerHour,
          isActive: formData.isActive,
          earnsClashPoints: formData.earnsClashPoints,
        });
      } else {
        await createCategory({
          name: formData.name.trim(),
          pricePerHour: formData.pricePerHour,
          isActive: formData.isActive,
          earnsClashPoints: formData.earnsClashPoints,
        });
      }
      handleClose(true);
    } catch (error: any) {
      setErrorMessage(error?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={() => handleClose()}
      title={props.category ? "Edit Category" : "Create Category"}
      placement="side"
      size="sm"
      busy={loading}
      bodyClassName="category-dialog__body"
      footer={
        <>
          <Button variant="ghost" onClick={() => handleClose()} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            {props.category ? "Update" : "Create"}
          </Button>
        </>
      }
    >
      {errorMessage && <Alert tone="danger">{errorMessage}</Alert>}

      <Field label="Category Name" required>
        {(id) => (
          <Input
            id={id}
            type="text"
            placeholder="e.g., VIP, Academy"
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            disabled={loading}
          />
        )}
      </Field>

      <Field label="Price Per Hour (EGP)" required>
        {(id) => (
          <Input
            id={id}
            type="number"
            min="0"
            step="50"
            placeholder="350"
            value={formData.pricePerHour}
            onChange={(e) => handleChange("pricePerHour", parseFloat(e.target.value) || 0)}
            disabled={loading}
          />
        )}
      </Field>

      <Checkbox
        checked={formData.isActive}
        onChange={(checked) => handleChange("isActive", checked)}
        disabled={loading}
        label="Active"
      />

      <Checkbox
        checked={formData.earnsClashPoints}
        onChange={(checked) => handleChange("earnsClashPoints", checked)}
        disabled={loading}
        label="Earns Clash Points"
      />
    </Dialog>
  );
};

export default CategoryDialog;
